import type { BusinessDate } from '../period/index.js';

export type RenewalKind = 'domain' | 'hosting' | 'amc' | 'ssl' | 'other';

export const RENEWAL_KINDS: readonly RenewalKind[] = ['domain', 'hosting', 'amc', 'ssl', 'other'];

export const RENEWAL_KIND_LABEL: Record<RenewalKind, string> = {
  domain: 'Domain',
  hosting: 'Hosting',
  amc: 'AMC',
  ssl: 'SSL certificate',
  other: 'Other renewal',
};

export const DEFAULT_REMIND_DAYS: readonly number[] = [60, 30, 7, 0];

const MS_PER_DAY = 24 * 60 * 60 * 1000;

function utcMidnight(iso: string): number {
  const [y, m, d] = iso.split('-').map(Number);
  return Date.UTC(y!, m! - 1, d!);
}

export function daysUntil(today: BusinessDate, expiresOn: BusinessDate): number {
  return Math.round((utcMidnight(expiresOn) - utcMidnight(today)) / MS_PER_DAY);
}

export function normalizeRemindDays(days: readonly number[]): number[] {
  const clean = days.filter((d) => Number.isInteger(d) && d >= 0 && d <= 730).sort((a, b) => b - a);
  return [...new Set(clean)];
}

/**
 * Offsets whose reminder is now owed: every configured offset the expiry has
 * come within, minus those already sent for this expiry date. A scan that
 * skipped days catches up here instead of losing the reminder, and the
 * caller sends one message while logging every offset returned.
 */
export function unsentDueOffsets(
  daysLeft: number,
  remindDays: readonly number[],
  sentOffsets: readonly number[],
): number[] {
  const sent = new Set(sentOffsets);
  return normalizeRemindDays(remindDays).filter((o) => daysLeft <= o && !sent.has(o));
}

export type RenewalUrgency = 'expired' | 'today' | 'critical' | 'soon' | 'later';

export function renewalUrgency(daysLeft: number): RenewalUrgency {
  if (daysLeft < 0) return 'expired';
  if (daysLeft === 0) return 'today';
  if (daysLeft <= 7) return 'critical';
  if (daysLeft <= 30) return 'soon';
  return 'later';
}

export function describeDaysLeft(daysLeft: number): string {
  if (daysLeft < 0) {
    const n = -daysLeft;
    return `expired ${n} day${n === 1 ? '' : 's'} ago`;
  }
  if (daysLeft === 0) return 'expires today';
  if (daysLeft === 1) return 'expires tomorrow';
  return `expires in ${daysLeft} days`;
}

export function addMonths(date: BusinessDate, months: number): BusinessDate {
  const [y, m, d] = date.split('-').map(Number);
  const target = new Date(Date.UTC(y!, m! - 1 + months, 1));
  const lastDay = new Date(
    Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0),
  ).getUTCDate();
  target.setUTCDate(Math.min(d!, lastDay));
  return target.toISOString().slice(0, 10) as BusinessDate;
}
